<template>
  <div class="d-flex w-100 flex-column gap-xs step pa-xs justify-space-between">
    <div class="d-flex gap-sm align-center">
      <PersonalizeProgress :current-step="currentStep" :total-steps="totalSteps" />
      <h1 class="font-md blue-darker bold-medium text-align-left">{{ getCurrentSpaceName }}</h1>
    </div>

    <div>
      <img class="img-question-space border-default" :src="getCurrentSpaceImage"  alt="img-question-space"/>
    </div>

    <div v-for="(question, idx) in spaceQuestion.questions" :key="`space-question-${idx}`">
      <p class="text-align-left font-sm mb-sm">{{ question.label }}</p>
      <AnswerOptions
        :question="question"
        :block="type === 'check-out'"
        :flex="true"
        :type="type"
        :booking-id="bookingId"
      />
      <div v-if="type === 'check-out'">
        <UploadIncidentImages
          :files="question.images"
          :is-loading="isLoading"
          @upload-file="(v) => handleUploadImage(v, question)"
          @remove-file="(v) => removeImage(v, question)"
        />
      </div>
    </div>

    <div class="d-flex justify-space-between d-flex justify-space-between mt-xs w-100">
      <ion-button
        fill="clear"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="$emit('back')"
      >
        {{ t('global.back') }}
        <ion-icon slot="start" :icon="chevronBackOutline" aria-hidden="true"></ion-icon>
      </ion-button>

      <ion-button
        :disabled="getDisabled"
        fill="solid"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="$emit('continue')"
      >
        <ion-icon slot="end" :icon="chevronForwardOutline" aria-hidden="true"></ion-icon>
        {{ t('global.continue') }}
      </ion-button>
    </div>
  </div>
</template>
<script setup lang="ts">
  import { IonButton, IonIcon } from '@ionic/vue';
  import PersonalizeProgress from '../personalize/PersonalizeProgress.vue';
  import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
  import { useI18n } from 'vue-i18n';
  import { computed, ref } from 'vue';
  import AnswerOptions from './AnswerOptions.vue';
  import { storeToRefs } from 'pinia';
  import { useCheckInStore } from '@/stores/checkin-store';
  import { Photos, RentalUnit } from '@/interfaces/elastic/elastic.interface';
  import { useSearch } from '@/composables/useSearch';
  import UploadIncidentImages from './UploadIncidentImages.vue';
  import { useCheck } from '@/composables/useCheck';
  import { GeneralQuestion, SpaceQuestion } from '@/interfaces/check/check-in.interface';
  import { useProfileStore } from '@/stores/profile-store';

  const props = withDefaults(
    defineProps<{
      spaceQuestion: SpaceQuestion;
      currentStep: number;
      totalSteps: number;
      type: string;
      bookingId: string;
    }>(),
    {},
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const emit = defineEmits(['continue', 'back']);
  const { rentalUnit } = storeToRefs(useCheckInStore());
  const { uploadImage } = useCheck();
  const { getCurrentName, getCoverImage } = useSearch();
  const isLoading = ref(false);
  const { languageSelected } = storeToRefs(useProfileStore());

  const { t } = useI18n();

  const getCurrentSpaceName = computed(() => {
    if (!props.spaceQuestion.SpaceId) {
      return getCurrentName(rentalUnit.value as RentalUnit, languageSelected.value) as string;
    }
    const spaceSelected = getSpaceById(props.spaceQuestion.SpaceId);
    return spaceSelected ? spaceSelected.Space.name : '';
  });

  const getDisabled = computed(() => {
    return (
      (props.type === 'check-out' &&
        props.spaceQuestion.questions.some((question) => question.images?.length === 0)) ||
      isLoading.value
    );
  });

  const getCurrentSpaceImage = computed(() => {
    if (!props.spaceQuestion.SpaceId && rentalUnit.value?.Images) {
      return getCoverImage(
        rentalUnit.value?.Images.length > 0
          ? rentalUnit.value?.Images
          : (rentalUnit.value?.Property?.Images as Photos[]),
      );
    }
    const spaceSelected = getSpaceById(props.spaceQuestion.SpaceId as string);
    return spaceSelected && spaceSelected.Images[0]
      ? spaceSelected.Images[0].image
      : 'https://st-web.lodgerin.com/images/image-not-found.webp';
  });

  const getSpaceById = (spaceId: string) => {
    const space = rentalUnit.value?.SpacesExtras.find((spac) => spac.id === spaceId);
    return space ? space.SpacesData : null;
  };

  const removeImage = (index: number, question: GeneralQuestion) => {
    question.images?.splice(index, 1);
  };

  const handleUploadImage = async (img64: string, question: GeneralQuestion) => {
    isLoading.value = true;
    const data = await uploadImage(img64, question, props.bookingId, props.type);

    if (data) {
      question.images?.push(data.urlFile);
    }
    isLoading.value = false;
  };
</script>

<style lang="scss" scoped>
  ion-modal {
    padding: 10px;
    --height: 60%;
    --border-radius: 16px;
    --box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  }

  .img-question-space {
    width: 100% !important;
    max-height: 180px !important;
    height: 100%;
    object-fit: cover;
  }
</style>
